import { useNavigate } from 'react-router-dom';
import { Navbar } from '../components/Navbar';
import { Footer } from '../components/Footer';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '../components/ui/accordion';
import { 
  ArrowRight, 
  CheckCircle2, 
  Shield, 
  Brain, 
  Video, 
  Users, 
  Star,
  Zap,
  Globe,
  Clock,
  FileCheck,
  Mic,
  Camera,
  BarChart3
} from 'lucide-react';

export default function LandingPage() {
  const navigate = useNavigate();

  const features = [
    {
      icon: <Brain className="w-6 h-6" />,
      title: "AI-Powered Assessment",
      description: "Advanced AI analyzes your responses for keyword coverage, clarity, and relevance."
    },
    {
      icon: <Video className="w-6 h-6" />,
      title: "Realistic Interview Simulation",
      description: "Experience real interview conditions with timed questions and video recording."
    },
    {
      icon: <Shield className="w-6 h-6" />,
      title: "Identity Verification",
      description: "Secure multi-step verification including facial recognition and liveness detection."
    },
    {
      icon: <BarChart3 className="w-6 h-6" />,
      title: "Detailed Scoring",
      description: "Get comprehensive feedback with per-question scores and improvement suggestions."
    },
    {
      icon: <Globe className="w-6 h-6" />,
      title: "University Partnerships",
      description: "Trusted by leading universities worldwide for credibility interviews."
    },
    {
      icon: <Clock className="w-6 h-6" />,
      title: "Instant Results",
      description: "Receive your interview results and transcript immediately after completion."
    }
  ];

  const howItWorks = [
    {
      step: "01",
      title: "Sign Up & Choose Plan",
      description: "Create your account and select a package that fits your needs."
    },
    {
      step: "02",
      title: "Complete Verification",
      description: "Verify your identity through our secure multi-step process."
    },
    {
      step: "03",
      title: "Take Your Interview",
      description: "Answer questions in a simulated interview environment."
    },
    {
      step: "04",
      title: "Get Results",
      description: "Receive detailed AI-powered assessment and feedback."
    }
  ];

  const testimonials = [
    {
      name: "Sarah Chen",
      role: "Student, University of Toronto",
      content: "Visainterviews.ai helped me prepare for my credibility interview with confidence. The AI feedback was incredibly helpful!",
      rating: 5
    },
    {
      name: "Michael O'Brien",
      role: "Education Agent",
      content: "As an agent, this platform has revolutionized how I prepare my students. The detailed analytics are invaluable.",
      rating: 5
    },
    {
      name: "Priya Sharma",
      role: "Student, LSE",
      content: "The realistic simulation and immediate feedback helped me identify and improve my weak areas before the actual interview.",
      rating: 5
    }
  ];

  const faqs = [
    {
      question: "What is a credibility interview?",
      answer: "A credibility interview is conducted by universities to verify a student's genuine intent to study and their understanding of their chosen program. Our platform helps you prepare for these interviews with realistic simulations."
    },
    {
      question: "How does the AI assessment work?",
      answer: "Our AI analyzes your responses for keyword coverage, relevance to the question, clarity of expression, and overall coherence. You receive detailed feedback on each answer including areas for improvement."
    },
    {
      question: "Is my data secure?",
      answer: "Yes, we take security seriously. All interview recordings are encrypted and stored securely. We comply with international data protection standards and never share your data with third parties."
    },
    {
      question: "Can I retake an interview?",
      answer: "Each interview credit allows for one complete interview session. You can purchase additional credits if you want more practice sessions or need to retake an interview."
    },
    {
      question: "What verification steps are required?",
      answer: "Our verification process includes identity document verification, facial recognition matching, liveness detection, and consent recording. This ensures the integrity of each interview."
    }
  ];

  return (
    <div className="min-h-screen flex flex-col">
      <Navbar />
      
      {/* Hero Section */}
      <section className="pt-24 pb-16 md:pt-32 md:pb-24 px-4 sm:px-6 lg:px-8 overflow-hidden">
        <div className="max-w-7xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className="animate-slide-up">
              <Badge className="mb-4 bg-lime-100 text-lime-700 dark:bg-lime-900/30 dark:text-lime-400 hover:bg-lime-100">
                AI-Powered Interview Platform
              </Badge>
              <h1 className="font-heading font-extrabold text-4xl sm:text-5xl lg:text-6xl tracking-tight leading-tight mb-6">
                AI-Visa Interview
                <span className="text-lime-500"> Practice System</span>
              </h1>
              <p className="text-lg md:text-xl text-muted-foreground mb-8 max-w-xl">
                A smart way to prepare for your CAS Shield Interviews, Immigration Interviews, and IELTS Speaking Tests in a real interview environment.
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <Button 
                  size="lg" 
                  className="bg-lime-500 hover:bg-lime-600 text-white gap-2 rounded-full px-8"
                  onClick={() => navigate('/register')}
                  data-testid="hero-cta-start"
                >
                  Register
                  <ArrowRight className="w-4 h-4" />
                </Button>
                <Button 
                  size="lg" 
                  variant="outline"
                  className="rounded-full px-8"
                  onClick={() => navigate('/pricing')}
                  data-testid="hero-cta-pricing"
                >
                  View Pricing
                </Button>
              </div>
              {/* Trust indicators */}
              <div className="mt-8 flex items-center gap-4">
                <div className="flex -space-x-2">
                  <img src="https://images.unsplash.com/photo-1494790108377-be9c29b29330?w=40&h=40&fit=crop&crop=face" alt="User" className="w-8 h-8 rounded-full border-2 border-background" />
                  <img src="https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=40&h=40&fit=crop&crop=face" alt="User" className="w-8 h-8 rounded-full border-2 border-background" />
                  <img src="https://images.unsplash.com/photo-1438761681033-6461ffad8d80?w=40&h=40&fit=crop&crop=face" alt="User" className="w-8 h-8 rounded-full border-2 border-background" />
                  <div className="w-8 h-8 rounded-full border-2 border-background bg-lime-500 flex items-center justify-center text-white text-xs font-medium">+</div>
                </div>
                <div>
                  <p className="text-2xl font-bold">3.7k</p>
                  <p className="text-xs text-muted-foreground">Interviews conducted</p>
                </div>
              </div>
            </div>
            
            {/* Hero Image Grid - Video Call Style */}
            <div className="relative animate-slide-up stagger-2">
              <div className="absolute inset-0 bg-gradient-to-br from-lime-400/10 to-lime-500/10 rounded-3xl blur-3xl"></div>
              
              {/* Main grid layout */}
              <div className="relative grid grid-cols-12 gap-3">
                {/* Main video call image */}
                <div className="col-span-7 row-span-2 relative">
                  <img 
                    src="https://images.pexels.com/photos/6722641/pexels-photo-6722641.jpeg?auto=compress&cs=tinysrgb&w=600" 
                    alt="Student in video interview"
                    className="rounded-2xl shadow-xl w-full h-full object-cover aspect-[3/4]"
                    data-testid="hero-main-image"
                  />
                  {/* Video call controls overlay */}
                  <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex items-center gap-2 bg-black/60 backdrop-blur-sm rounded-full px-4 py-2">
                    <div className="w-8 h-8 rounded-full bg-white/20 flex items-center justify-center">
                      <Mic className="w-4 h-4 text-white" />
                    </div>
                    <div className="w-10 h-10 rounded-full bg-red-500 flex items-center justify-center">
                      <Video className="w-5 h-5 text-white" />
                    </div>
                    <div className="w-8 h-8 rounded-full bg-white/20 flex items-center justify-center">
                      <Camera className="w-4 h-4 text-white" />
                    </div>
                  </div>
                </div>
                
                {/* Stats card */}
                <div className="col-span-5 bg-lime-100 dark:bg-lime-950/50 rounded-2xl p-4 flex flex-col justify-center">
                  <p className="text-3xl md:text-4xl font-bold text-lime-600 dark:text-lime-400">38%</p>
                  <p className="text-xs text-muted-foreground mt-1">
                    Students <span className="text-red-500 font-medium">fail</span> CAS Shield interview, so it's better to practice beforehand
                  </p>
                </div>
                
                {/* Second person image */}
                <div className="col-span-5 relative">
                  <img 
                    src="https://images.pexels.com/photos/4226140/pexels-photo-4226140.jpeg?auto=compress&cs=tinysrgb&w=400" 
                    alt="Online interview session"
                    className="rounded-2xl shadow-lg w-full h-full object-cover aspect-square"
                    data-testid="hero-secondary-image"
                  />
                </div>
              </div>
              
              {/* Floating stat card */}
              <div className="absolute -bottom-4 -left-4 glass-card rounded-xl p-4 bg-white/90 dark:bg-slate-900/90 backdrop-blur-lg border border-white/20 dark:border-slate-800 shadow-xl animate-scale-in stagger-3">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-lime-500/20 flex items-center justify-center">
                    <Zap className="w-5 h-5 text-lime-500" />
                  </div>
                  <div>
                    <p className="text-sm font-semibold">95%</p>
                    <p className="text-xs text-muted-foreground">Success Rate</p>
                  </div>
                </div>
              </div>
              
              {/* Floating rating card */}
              <div className="absolute -top-4 -right-4 glass-card rounded-xl p-4 bg-white/90 dark:bg-slate-900/90 backdrop-blur-lg border border-white/20 dark:border-slate-800 shadow-xl animate-scale-in stagger-4">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-lime-500/20 flex items-center justify-center">
                    <Star className="w-5 h-5 text-lime-500" />
                  </div>
                  <div>
                    <p className="text-sm font-semibold">4.9/5 Rating</p>
                    <p className="text-xs text-muted-foreground">Student Reviews</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-16 md:py-24 px-4 sm:px-6 lg:px-8 bg-muted/30">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <Badge className="mb-4 bg-lime-100 text-lime-700 dark:bg-lime-900/30 dark:text-lime-400">
              Features
            </Badge>
            <h2 className="font-heading font-bold text-3xl md:text-4xl tracking-tight mb-4">
              Everything You Need to Succeed
            </h2>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
              Our comprehensive platform provides all the tools you need to prepare for and ace your credibility interview.
            </p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {features.map((feature, index) => (
              <Card 
                key={index} 
                className="bg-card hover:shadow-lg transition-shadow border-border/50"
                data-testid={`feature-card-${index}`}
              >
                <CardHeader>
                  <div className="w-12 h-12 rounded-lg bg-lime-500/10 flex items-center justify-center text-lime-500 mb-4">
                    {feature.icon}
                  </div>
                  <CardTitle className="font-heading text-lg">{feature.title}</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-muted-foreground">{feature.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className="py-16 md:py-24 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <Badge className="mb-4 bg-lime-100 text-lime-700 dark:bg-lime-900/30 dark:text-lime-400">
              Process
            </Badge>
            <h2 className="font-heading font-bold text-3xl md:text-4xl tracking-tight mb-4">
              How It Works
            </h2>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
              Get started in minutes with our simple four-step process.
            </p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {howItWorks.map((item, index) => (
              <div key={index} className="relative" data-testid={`how-it-works-${index}`}>
                <div className="text-6xl font-heading font-extrabold text-lime-500/10 absolute -top-4 -left-2">
                  {item.step}
                </div>
                <div className="relative pt-8">
                  <h3 className="font-heading font-semibold text-lg mb-2">{item.title}</h3>
                  <p className="text-muted-foreground">{item.description}</p>
                </div>
                {index < howItWorks.length - 1 && (
                  <div className="hidden lg:block absolute top-1/2 right-0 w-12 h-0.5 bg-border translate-x-6"></div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Interview Features Visual */}
      <section className="py-16 md:py-24 px-4 sm:px-6 lg:px-8 bg-muted/30">
        <div className="max-w-7xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <Badge className="mb-4 bg-lime-100 text-lime-700 dark:bg-lime-900/30 dark:text-lime-400">
                Interview Room
              </Badge>
              <h2 className="font-heading font-bold text-3xl md:text-4xl tracking-tight mb-6">
                Professional Interview Experience
              </h2>
              <p className="text-lg text-muted-foreground mb-8">
                Our interview room provides a realistic simulation with all the features you need for a successful interview.
              </p>
              <div className="space-y-4">
                {[
                  { icon: <Camera className="w-5 h-5" />, text: "HD Video Recording" },
                  { icon: <Mic className="w-5 h-5" />, text: "Clear Audio Capture" },
                  { icon: <Clock className="w-5 h-5" />, text: "Timed Questions (30s read, 90s answer)" },
                  { icon: <FileCheck className="w-5 h-5" />, text: "Instant Transcription" },
                ].map((item, index) => (
                  <div key={index} className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-lg bg-lime-500/10 flex items-center justify-center text-lime-500">
                      {item.icon}
                    </div>
                    <span className="font-medium">{item.text}</span>
                  </div>
                ))}
              </div>
            </div>
            <div className="relative">
              <img 
                src="https://images.unsplash.com/photo-1637249820580-a877474a889d?crop=entropy&cs=srgb&fm=jpg&q=85&w=800" 
                alt="Interview room simulation"
                className="rounded-2xl shadow-xl w-full object-cover aspect-video"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-16 md:py-24 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <Badge className="mb-4 bg-lime-100 text-lime-700 dark:bg-lime-900/30 dark:text-lime-400">
              Testimonials
            </Badge>
            <h2 className="font-heading font-bold text-3xl md:text-4xl tracking-tight mb-4">
              What Our Users Say
            </h2>
          </div>
          <div className="grid md:grid-cols-3 gap-6">
            {testimonials.map((testimonial, index) => (
              <Card key={index} className="bg-card border-border/50" data-testid={`testimonial-${index}`}>
                <CardContent className="pt-6">
                  <div className="flex gap-1 mb-4">
                    {[...Array(testimonial.rating)].map((_, i) => (
                      <Star key={i} className="w-4 h-4 fill-lime-500 text-lime-500" />
                    ))}
                  </div>
                  <p className="text-muted-foreground mb-4">"{testimonial.content}"</p>
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-lime-500/20 flex items-center justify-center">
                      <Users className="w-5 h-5 text-lime-500" />
                    </div>
                    <div>
                      <p className="font-semibold text-sm">{testimonial.name}</p>
                      <p className="text-xs text-muted-foreground">{testimonial.role}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-16 md:py-24 px-4 sm:px-6 lg:px-8 bg-muted/30">
        <div className="max-w-3xl mx-auto">
          <div className="text-center mb-12">
            <Badge className="mb-4 bg-lime-100 text-lime-700 dark:bg-lime-900/30 dark:text-lime-400">
              FAQ
            </Badge>
            <h2 className="font-heading font-bold text-3xl md:text-4xl tracking-tight mb-4">
              Frequently Asked Questions
            </h2>
          </div>
          <Accordion type="single" collapsible className="w-full">
            {faqs.map((faq, index) => (
              <AccordionItem key={index} value={`item-${index}`} data-testid={`faq-${index}`}>
                <AccordionTrigger className="text-left font-heading font-medium">
                  {faq.question}
                </AccordionTrigger>
                <AccordionContent className="text-muted-foreground">
                  {faq.answer}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 md:py-24 px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="font-heading font-bold text-3xl md:text-4xl tracking-tight mb-6">
            Ready to Ace Your Interview?
          </h2>
          <p className="text-lg text-muted-foreground mb-8 max-w-2xl mx-auto">
            Join thousands of students who have successfully prepared for their university interviews with our platform.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button 
              size="lg" 
              className="bg-lime-500 hover:bg-lime-600 text-white gap-2"
              onClick={() => navigate('/register')}
              data-testid="cta-get-started"
            >
              Get Started Free
              <ArrowRight className="w-4 h-4" />
            </Button>
            <Button 
              size="lg" 
              variant="outline"
              onClick={() => navigate('/contact')}
            >
              Contact Sales
            </Button>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
